import React, { useState } from 'react';
import { X, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { useBrands } from '../../hooks/useBrands';
import 'react-datepicker/dist/react-datepicker.css';

interface IdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (idea: {
    title: string;
    description: string;
    brandId: string;
    reminderAt: string | null;
  }) => void;
  /** Pre-fill fields when saving from AI content ideas */
  initialTitle?: string;
  initialDescription?: string;
  initialBrandId?: string;
  /** Pre-fill the reminder date (e.g. when clicking a calendar day) */
  initialDate?: Date;
  isLoading?: boolean;
}

export const IdeaModal: React.FC<IdeaModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTitle = '',
  initialDescription = '',
  initialBrandId = '',
  initialDate,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const { data: brandsData } = useBrands();
  const brands = brandsData?.brands || [];

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [brandId, setBrandId] = useState(initialBrandId || (brands.length === 1 ? brands[0]?.id : ''));
  const [reminderDate, setReminderDate] = useState<Date | null>(initialDate || null);

  // Reset form when modal opens with new initial values
  React.useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setDescription(initialDescription);
      setBrandId(initialBrandId || (brands.length === 1 ? brands[0]?.id : ''));
      setReminderDate(initialDate || null);
    }
  }, [isOpen, initialTitle, initialDescription, initialBrandId, initialDate, brands]);

  const handleSave = () => {
    if (!title.trim() || !brandId) return;
    onSave({
      title: title.trim(),
      description: description.trim(),
      brandId,
      reminderAt: reminderDate ? reminderDate.toISOString() : null
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-black">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              <h2 className="text-xl font-semibold">
                {t('ideaModal.title', 'Save a Post Idea')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="idea-title" className="block text-sm font-medium text-gray-700 mb-1">
                {t('ideaModal.ideaTitle', 'Idea title')} *
              </label>
              <input
                id="idea-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('ideaModal.titlePlaceholder', 'e.g. Behind-the-scenes reel of our process')}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="idea-description" className="block text-sm font-medium text-gray-700 mb-1">
                {t('ideaModal.description', 'Notes')}
              </label>
              <textarea
                id="idea-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('ideaModal.descriptionPlaceholder', 'Quick notes about this idea...')}
                rows={3}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
              />
            </div>

            {/* Brand */}
            <div>
              <label htmlFor="idea-brand" className="block text-sm font-medium text-gray-700 mb-1">
                {t('ideaModal.brand', 'Brand')} *
              </label>
              <select
                id="idea-brand"
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
              >
                <option value="">{t('ideaModal.selectBrand', 'Select a brand')}</option>
                {brands.map((brand: any) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Reminder date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('ideaModal.reminderDate', 'Remind me on')}
              </label>
              <DatePicker
                selected={reminderDate}
                onChange={(date: Date | null) => setReminderDate(date)}
                dateFormat="PPP"
                minDate={new Date()}
                placeholderText={t('ideaModal.reminderPlaceholder', 'Pick a date (optional)')}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm"
                isClearable
                inline
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('ideaModal.reminderHint', 'The idea will appear on your calendar on this date.')}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex flex-row-reverse gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={!title.trim() || !brandId || isLoading}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-500 text-base font-medium text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('common.saving', 'Saving...')
                : t('ideaModal.save', 'Save Idea')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:text-sm"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
